import { set } from 'date-fns';
import { format } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import Modal from '~/components/Modal/Modal';
import { useDateLang } from '~/hooks/useDateLang';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';
import { useTimeFormat } from '~/hooks/useTimeFormat';

import { orderStore } from '~/stores';

import styles from './ModalDeliveryInfoCD.module.scss';

const ModalDeliveryInfoCD = observer(() => {
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();

  const expressDelivery = orderStore.expressDelivery;
  const scheduledDelivery = orderStore.scheduledDelivery;
  const deliveryScheduleLimits = orderStore.slotDeliverySchedule;

  const parseTime = (time?: string) => {
    if (!time) {
      return '';
    }

    const [hours, minutes] = time.split(':');

    const result = format(
      set(Date.now(), { hours: +hours!, minutes: +minutes! }),
      timeFormat,
      {
        timeZone: company.config.timeZone,
        locale,
      },
    );

    if (result === '23:59') {
      return t('midnight');
    }

    return result;
  };

  const handleCloseModal = () => {
    closeModal();
  };

  return (
    <Modal
      isShow
      className={styles.deliveryTerms}
      onBackdropDismiss={handleCloseModal}
    >
      <div className={styles.deliveryTerms}>
        <header className={styles.deliveryTerms__header}>
          <h2>{t('deliveryTerms')}</h2>
          <button className="button" onClick={handleCloseModal}>
            <Icon type="close" size={isMobile ? 24 : 40} />
          </button>
        </header>
        <div className={styles.deliveryTerms__body}>
          <div className={styles.section}>
            {expressDelivery && scheduledDelivery && (
              <p>{t('deliveryOffers')}:</p>
            )}
            <ul>
              <li>
                <div>
                  <span className={styles.express}>{t('expressDelivery')}</span>{' '}
                  – {t('whenTimeMattersMost')}
                </div>
              </li>
            </ul>
            <p>
              <span className={styles.bold}>{t('needItFast')}</span>{' '}
              <span>{t('expressDeliveryDescription')}</span>
            </p>
            {expressDelivery && (
              <p className={styles.unavailable}>
                {t('expressDeliveryAvailability', {
                  from: parseTime(expressDelivery.opening),
                  to: parseTime(expressDelivery.closing),
                })}
              </p>
            )}
            <ul>
              <li>
                <div>
                  <span className={styles.scheduled}>
                    {t('standardDelivery')}
                  </span>{' '}
                  - {t('standardDeliveryInfo')}
                </div>
              </li>
            </ul>
            {scheduledDelivery && deliveryScheduleLimits && (
              <p>
                <span className={styles.bold}>{t('standardDelivery')}</span>{' '}
                {t('scheduleDeliveryTime', {
                  from: parseTime(deliveryScheduleLimits.opening),
                  to: parseTime(deliveryScheduleLimits.closing),
                })}
              </p>
            )}
            <p>{t('orderMayBeSplit')}</p>
            <p>{t('pleaseShowYourId')}</p>
          </div>

          <h3>{t('deliveryPaymentTerms')}</h3>
          <div className={styles.deliveryFee}>
            <p>{t('deliveryFeeVary')}</p>
            <p>{t('exactFee')}</p>
            <p>{t('goToCharity')}</p>
          </div>
          <p className={styles.small}>{t('supportFairPay')}</p>
        </div>
        <footer className={styles.deliveryTerms__footer}>
          <button className="button _primary" onClick={handleCloseModal}>
            {t('okButton')}
          </button>
        </footer>
      </div>
    </Modal>
  );
});

export default ModalDeliveryInfoCD;
